import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { Text } from "native-base";
import FastImage from "@d11/react-native-fast-image";
import { webFont } from "../../Universal/API/API";
import PressableWrapp from "../../Universal/Buttons/PressableReact/PressableWrapp";
import { CollectionsDeanteData } from "../../Universal/API/Context-API/AppProvider";

interface CollectionPreviewCardProps {
  collection: CollectionsDeanteData;
  scaleAll: number;
  width: string;
  onPress: () => void;
  onOpenPdf?: () => void;
}

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    card: {
      borderRadius: 18 * scaleAll,
      overflow: "hidden",
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
    },
    cardHovered: {
      shadowColor: "#000000",
      shadowOpacity: 0.14,
      shadowRadius: 28,
      shadowOffset: { width: 0, height: 14 },
      elevation: 10,
    },
    mediaWrap: {
      position: "relative",
      width: "100%",
      aspectRatio: 1,
      overflow: "hidden",
      backgroundColor: "#F4F1EB",
    },
    image: {
      width: "100%",
      height: "100%",
    },
    overlay: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: 12 * scaleAll,
      paddingTop: 24 * scaleAll,
      paddingBottom: 12 * scaleAll,
      backgroundImage:
        "linear-gradient(to top, rgba(17,17,17,0.82), rgba(17,17,17,0.44), rgba(17,17,17,0.02))",
    } as any,
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 18 * scaleAll,
      lineHeight: 21 * scaleAll,
      color: "#FFFFFF",
      fontWeight: "700",
      marginBottom: 4 * scaleAll,
    },
    metaRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8 * scaleAll,
    },
    metaChip: {
      minHeight: 24 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 8 * scaleAll,
      paddingVertical: 4 * scaleAll,
      backgroundColor: "rgba(255,255,255,0.9)",
      alignItems: "center",
      justifyContent: "center",
    },
    metaChipDark: {
      backgroundColor: "#111111",
    },
    metaChipText: {
      fontFamily: webFont("Inter"),
      fontSize: 9 * scaleAll,
      color: "#3C3732",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.45,
    },
    metaChipTextDark: {
      color: "#FFFFFF",
    },
  });

const CollectionPreviewCard: React.FC<CollectionPreviewCardProps> = ({
  collection,
  scaleAll,
  width,
  onPress,
  onOpenPdf,
}) => {
  const styles = React.useMemo(() => createStyles(scaleAll), [scaleAll]);
  const [hovered, setHovered] = React.useState(false);

  const coverImage =
    collection.collection_small_image_slides_small_mmg?.[0] ||
    collection.collection_full_image_gallery_slides?.[0] ||
    collection.collection_small_image_gallery_slides_s?.[0] ||
    null;

  const unitsCount = collection.units_ids?.length ?? 0;
  const galleryCount = collection.collection_full_image_gallery_slides?.length ?? 0;

  return (
    <PressableWrapp
      onPress={onPress}
      activeOpacity={0.86}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        width,
      }}
    >
      <View style={[styles.card, hovered ? styles.cardHovered : null]}>
        <View style={styles.mediaWrap}>
          {coverImage ? (
            <FastImage
              source={{ uri: coverImage, cache: "immutable" as any }}
              fallback={true}
              style={styles.image}
              resizeMode="cover"
            />
          ) : null}

          <View style={styles.overlay}>
            <Text selectable={false} style={styles.title}>
              {collection.collection_name}
            </Text>

            <View style={styles.metaRow}>
              <View style={styles.metaChip}>
                <Text selectable={false} style={styles.metaChipText}>
                  {unitsCount} поз.
                </Text>
              </View>
              <View style={styles.metaChip}>
                <Text selectable={false} style={styles.metaChipText}>
                  {galleryCount} фото
                </Text>
              </View>
              {!!collection.link_pdf && (
                <Pressable
                  onPress={(event: any) => {
                    event?.stopPropagation?.();
                    event?.preventDefault?.();
                    onOpenPdf?.();
                  }}
                  style={[styles.metaChip, styles.metaChipDark]}
                >
                  <Text selectable={false} style={[styles.metaChipText, styles.metaChipTextDark]}>
                    PDF
                  </Text>
                </Pressable>
              )}
            </View>
          </View>
        </View>
      </View>
    </PressableWrapp>
  );
};

export default React.memo(CollectionPreviewCard);
